import React, { createContext, useContext, useReducer, useMemo } from "react";
import PropTypes from "prop-types";
import { profile as globalProfile } from "./profile";
import { updateUserProfile } from "utils/requests/user/userupdate";

/** Context for user profile */
const ProfileContext = createContext();
ProfileContext.displayName = "ProfileContext";

/**
 * Reducer for profile state updates
 * @param {Object} state Current state
 * @param {Object} action { type: string, value?: any }
 * @returns {Object} New state
 */
function reducer(state, action) {
  switch (action.type) {
    case "SET_PROFILE":
      return { ...state, ...action.value };
    default:
      throw new Error(`Unhandled action type: ${action.type}`);
  }
}

/**
 * Provider for user profile context
 * @param {Object} props
 * @param {React.ReactNode} props.children
 * @returns {JSX.Element}
 */
export function ProfileProvider({ children }) {
  const [controller, dispatch] = useReducer(reducer, { ...globalProfile });
  const value = useMemo(() => [controller, dispatch], [controller]);

  return <ProfileContext.Provider value={value}>{children}</ProfileContext.Provider>;
}

ProfileProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

/**
 * Hook to access profile state and dispatch
 * @returns {[Object, React.Dispatch]} [state, dispatch]
 */
export function useProfileController() {
  const context = useContext(ProfileContext);
  if (!context) {
    throw new Error("useProfileController must be used within a ProfileProvider");
  }
  return context;
}

/**
 * Replace the profile state with a new object
 * @param {React.Dispatch} dispatch
 * @param {Object} value New profile values
 */
const setProfile = (dispatch, value) => dispatch({ type: "SET_PROFILE", value });

/**
 * Updates the profile in backend and syncs context & global object
 * @param {React.Dispatch} dispatch
 * @param {Object} payload { firstName, lastName, email, mobile }
 * @returns {Promise<boolean>} True if update succeeded
 */
const updateProfile = async (dispatch, payload) => {
  try {
    const response = await updateUserProfile(payload);
    if (!response) return false;

    const updatedFields = {
      first_name: response.firstName || response.first_name,
      last_name: response.lastName || response.last_name,
      email: response.email,
      phone_number: response.mobile || response.phone_number,
      role: response.role,
    };

    Object.assign(globalProfile, updatedFields);
    dispatch({ type: "SET_PROFILE", value: updatedFields });

    return true;
  } catch (error) {
    return false;
  }
};

/**
 * Refreshes profile state from global profile object
 * @param {React.Dispatch} dispatch
 */
const refreshProfile = (dispatch) => {
  dispatch({ type: "SET_PROFILE", value: { ...globalProfile } });
};

export { setProfile, updateProfile, refreshProfile };
